import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { markNotificationRead } from "../../actions/notificationActions";
import { getNotificationBadgeLabel, getNotificationCategory } from "../../utils/notificationCategory";

const getScopeLabel = (scope, officeName = "") => {
    if (scope === "office") {
        return officeName ? `Офис · ${officeName}` : "Офис";
    }

    return "Личное";
};

const getTypeLabel = (type, meta = {}, data = {}) => {
    const severity = String(meta.severity || "").toLowerCase();

    if (type !== "system") return getNotificationBadgeLabel({ ...data, type, meta });
    if (type === "system" && severity === "success") return "Готово";
    if (type === "system" && severity === "info") return "Информация";
    if (type === "system" && severity === "warning") return "Внимание";
    if (type === "system" && severity === "error") return "Ошибка";
    return "Система";
};

// Задания в цвете заказов (своего цвета в стилях нет).
const normalizeType = (data = {}) => {
    const category = getNotificationCategory(data);
    return category === "task" ? "order" : category;
};

const normalizeSeverity = (severity) => {
    const value = String(severity || "").trim().toLowerCase();

    if (["success", "info", "warning", "error"].includes(value)) {
        return value;
    }

    return "";
};

const openExternalOrNavigate = (navigate, link) => {
    if (!link) return;

    if (/^https?:\/\//i.test(link)) {
        window.location.href = link;
        return;
    }

    navigate(link);
};

const NotificationToast = ({ data, onClose }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const offices = useSelector((state) => state.office?.offices || state.office?.items || []);
    const [visible, setVisible] = useState(false);

    const notification = useMemo(() => {
        if (!data || typeof data !== "object") return null;

        return {
            id: data._id || data.id || "",
            isRead: !!data.isRead,
            category: data.category || "",
            title: data.title || "Уведомление",
            message: data.message || "",
            createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
            link: data.link || "",
            officeId: String(
                data.office?._id || data.office?.id || data.officeId || data.meta?.officeId || ""
            ).trim(),
            scope: data.scope || data.meta?.scope || "personal",
            type: data.type || "system",
            meta: data.meta || {},
            severity: normalizeSeverity(data.severity || data.meta?.severity),
            officeName: String(
                data.office?.name ||
                data.meta?.officeName ||
                ""
            ).trim(),
        };
    }, [data]);

    useEffect(() => {
        if (!notification) {
            onClose?.();
            return undefined;
        }

        const openTimer = setTimeout(() => {
            setVisible(true);
        }, 30);

        const closeTimer = setTimeout(() => {
            setVisible(false);

            setTimeout(() => {
                onClose?.();
            }, 300);
        }, 8000);

        return () => {
            clearTimeout(openTimer);
            clearTimeout(closeTimer);
        };
    }, [notification, onClose]);

    if (!notification) return null;

    const handleClick = () => {
        if (notification.link) {
            // Перешли по уведомлению — значит, прочитано (как кнопка «Перейти» в списке).
            const realId = String(notification.id || "");
            if (realId && !notification.isRead && !realId.startsWith("scene-clipboard:")) {
                dispatch(markNotificationRead(realId, {
                    scope: notification.scope,
                    officeId: notification.officeId || null,
                }));
            }

            const targetOffice = notification.officeId && Array.isArray(offices)
                ? offices.find((item) => String(item?._id || item?.id || "") === notification.officeId)
                : null;
            const suspended = Boolean(
                targetOffice &&
                (targetOffice.suspended === true || targetOffice.billingSummary?.subscriptionActive === false)
            );

            if (suspended) {
                navigate(`/adminLayout?tab=myOffices&office=${encodeURIComponent(notification.officeId)}`);
            } else {
                openExternalOrNavigate(navigate, notification.link);
            }

            onClose?.();
            return;
        }

        setVisible(false);

        setTimeout(() => {
            onClose?.();
        }, 300);
    };

    const handleClose = (event) => {
        event.stopPropagation();

        setVisible(false);

        setTimeout(() => {
            onClose?.();
        }, 300);
    };

    const typeClass = normalizeType(notification);
    const severityClass = notification.severity
        ? `notification-toast-AO--severity-${notification.severity}`
        : "";

    return (
        <div
            className={[
                "notification-toast-AO",
                `notification-toast-AO--${notification.scope}`,
                `notification-toast-AO--type-${typeClass}`,
                severityClass,
                visible ? "is-visible" : "is-hidden",
            ].filter(Boolean).join(" ")}
            onClick={handleClick}
            role="status"
            aria-live="polite"
        >
            <div className="notification-toast-AO__header">
                <div className="notification-toast-AO__head-main">
                    <span className="notification-toast-AO__badge">
                        {getScopeLabel(notification.scope, notification.officeName)} · {getTypeLabel(notification.type, notification.meta, notification)}
                    </span>

                    <strong>{notification.title}</strong>
                </div>

                <div className="notification-toast-AO__side">
                    <span className="notification-toast-AO__time">
                        {notification.createdAt.toLocaleTimeString("ru-RU", {
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </span>

                    <button
                        type="button"
                        className="notification-toast-AO__close"
                        onClick={handleClose}
                        title="Закрыть"
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="notification-toast-AO__body">
                {notification.message}
            </div>
        </div>
    );
};

export default NotificationToast;
